/**
 * Ack-qty arithmetic for the PO line stepper.
 *
 * Vision already joins master `sku.case_size` onto GET /v1/orders/{id} as
 * `items[].caseSize` (PO `unitSize` is the fallback). Missing / non-positive
 * case size means the stepper stays ±1 and the "not a multiple" hint stays off.
 */

export function effectiveCaseSize(size: number | null | undefined): number | null {
  if (size == null || !Number.isFinite(size) || size <= 0) return null
  return Math.trunc(size)
}

export function clampLineQty(qty: number, max: number): number {
  const hi = Math.max(0, Math.trunc(max))
  if (!Number.isFinite(qty)) return 0
  return Math.min(hi, Math.max(0, Math.trunc(qty)))
}

export function stepLineQty(
  current: number,
  direction: 1 | -1,
  caseSize: number | null,
  max: number,
): number {
  const delta = effectiveCaseSize(caseSize) ?? 1
  const from = Number.isFinite(current) ? current : 0
  return clampLineQty(from + direction * delta, max)
}

/** True when qty is set and is not a multiple of a known case size. WARN, not FAIL. */
export function isNotCaseMultiple(
  qty: number | null | undefined,
  caseSize: number | null,
): boolean {
  const size = effectiveCaseSize(caseSize)
  if (size == null || qty == null || !Number.isFinite(qty)) return false
  return qty % size !== 0
}

/**
 * String shown in the qty field. Empty when committed qty is 0 so focus
 * never starts with a leading zero the operator then types through.
 */
export function committedQtyDraft(qty: number | null | undefined): string {
  if (qty == null || !Number.isFinite(qty)) return ''
  const n = Math.trunc(qty)
  return n === 0 ? '' : String(n)
}

/** Digits only; strip leading zeros (`08` → `8`, `010` → `10`) but keep a lone `0`. */
export function normalizeQtyDraft(raw: string): string {
  if (raw.includes('-')) return ''
  const digits = raw.replace(/[^\d]/g, '')
  if (digits === '') return ''
  return digits.replace(/^0+(?=\d)/, '')
}

/** Parse a focused draft. Empty string is “no value yet”, not 0. */
export function parseQtyDraft(draft: string): number | null {
  if (draft === '') return null
  const n = Number(draft)
  if (!Number.isFinite(n)) return null
  return Math.trunc(n)
}
